import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import GridComponent from './GridComponent';

// CSS is imported in the main grid-layout.css file

/**
 * GridLayout - Main layout component that manages the responsive grid system
 * 
 * This component provides a drag-and-drop, resizable grid system for all dashboard components.
 * It handles layout persistence, responsive breakpoints, and component management.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Grid components to render
 * @param {string} props.layoutKey - Unique key for saving/loading layouts (default: 'default')
 * @param {Object} props.defaultLayouts - Default layout configurations for different breakpoints
 * @param {boolean} props.isDraggable - Whether items can be dragged (default: true)
 * @param {boolean} props.isResizable - Whether items can be resized (default: true)
 * @param {function} props.onLayoutChange - Callback when layout changes
 * @param {Object} props.gridProps - Additional props to pass to ResponsiveGridLayout
 */
const GridLayout = ({
  children,
  layoutKey = 'default',
  defaultLayouts,
  isDraggable = true,
  isResizable = true,
  isActive = true,
  onLayoutChange,
  gridProps = {},
  ...otherProps
}) => {
  
  // Breakpoints for responsive design
  const breakpoints = {
    lg: 1200,
    md: 996,
    sm: 768,
    xs: 480,
    xxs: 0
  };

  // Default columns for each breakpoint
  const defaultCols = {
    lg: 12,
    md: 10,
    sm: 6,
    xs: 4,
    xxs: 2
  };

  // Validate layout items
  const validateLayoutItem = (item) => {
    if (!item || typeof item !== 'object') return false;
    const { i, x, y, w, h } = item;
    
    // Check if all required properties exist and are valid
    if (typeof i !== 'string' || typeof x !== 'number' || typeof y !== 'number' || 
        typeof w !== 'number' || typeof h !== 'number') return false;
    
    // Check basic constraints
    return x >= 0 && y >= 0 && w > 0 && h > 0 && !isNaN(x) && !isNaN(y) && !isNaN(w) && !isNaN(h);
  };

  // Load layouts - always use defaults (localStorage disabled)
  const loadLayouts = useCallback(() => {
    return defaultLayouts || {};
  }, [defaultLayouts]);

  const [layouts, setLayouts] = useState(loadLayouts);

  // Update layouts when defaultLayouts change - but preserve existing user modifications
  useEffect(() => {
    const newDefaultLayouts = loadLayouts();
    
    // Only update if we don't have any layouts yet, or if this is a completely new set of components
    setLayouts(prevLayouts => {
      // If no previous layouts exist, use the new defaults
      if (!prevLayouts || Object.keys(prevLayouts).length === 0) {
        return newDefaultLayouts;
      }
      
      // Otherwise, merge the new defaults with existing user modifications
      const mergedLayouts = {};
      Object.keys(newDefaultLayouts).forEach(breakpoint => {
        const newBreakpointLayout = newDefaultLayouts[breakpoint];
        const prevBreakpointLayout = prevLayouts[breakpoint] || [];
        
        // Create a map of existing components for efficient lookup
        const prevItemsMap = new Map();
        prevBreakpointLayout.forEach(item => {
          prevItemsMap.set(item.i, item);
        });
        
        // Start with new default layout, but preserve user modifications where they exist
        const mergedBreakpointLayout = newBreakpointLayout.map(newItem => {
          const existingItem = prevItemsMap.get(newItem.i);
          if (existingItem) {
            // Component exists in previous layouts - use the user-modified version
            return existingItem;
          }
          // New component - use the default layout
          return newItem;
        });
        
        mergedLayouts[breakpoint] = mergedBreakpointLayout;
      });
      
      return mergedLayouts;
    });
  }, [defaultLayouts, loadLayouts]);
  const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
  const [containerWidth, setContainerWidth] = useState(1200);
  const containerRef = useRef(null);

  // Measure container width
  useEffect(() => {
    const measureWidth = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerWidth(rect.width || 1200);
      }
    };

    measureWidth();
    
    const resizeObserver = new ResizeObserver(measureWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // When this tab becomes visible, remeasure synchronously before the browser paints
  // so the grid never renders a single frame at the wrong width.
  useLayoutEffect(() => {
    if (!isActive || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const w = rect.width || 1200;
    setContainerWidth(w);
  }, [isActive]);

  // Save layouts disabled - no localStorage persistence
  const saveLayouts = useCallback((newLayouts) => {
    // localStorage saving disabled - layouts won't persist across page reloads
  }, []);

  // Handle layout change
  const handleLayoutChange = useCallback((layout, allLayouts) => {
    setLayouts(allLayouts);
    saveLayouts(allLayouts);
    
    if (onLayoutChange) {
      onLayoutChange(layout, allLayouts);
    }
  }, [saveLayouts, onLayoutChange]);

  // Handle breakpoint change
  const handleBreakpointChange = useCallback((breakpoint, cols) => {
    setCurrentBreakpoint(breakpoint);
  }, []);


  // Use event interception to prevent drag unless from handle
  useEffect(() => {
    const handleMouseDown = (e) => {
      // FIRST: Check if click is inside a portaled modal/overlay
      // Prevents modal clicks from triggering tile dragging
      const isInModal = e.target.closest('.fixed.inset-0') ||
                        e.target.closest('[data-portal-overlay]');

      if (isInModal) {
        // Stop ALL events inside modals from reaching react-grid-layout
        e.stopPropagation();
        return; // Exit early - don't process grid logic for modal clicks
      }

      // THEN: Check if this mousedown is on a grid item but NOT on a drag handle or resize handle
      const gridItem = e.target.closest('.react-grid-item');
      const isHandle = e.target.closest('.grid-drag-handle');
      const isResizeHandle = e.target.closest('.react-resizable-handle');
      const isCytoscape = e.target.closest('canvas') || e.target.closest('[class*="cytoscape"]') || e.target.tagName === 'CANVAS';
      const isFullCalendar = e.target.closest('.fc');
      const isResourceCalendar = e.target.closest('.resource-calendar');

      const isInteractive = e.target.closest('button, input, select, textarea, a, [role="button"]');
      const isReactFlow = e.target.closest('.react-flow');

      if (gridItem && !isHandle && !isResizeHandle && !isCytoscape && !isFullCalendar && !isResourceCalendar && !isInteractive && !isReactFlow) {
        // Stop the event from reaching react-grid-layout to prevent unwanted dragging
        e.stopPropagation();
      }
    };

    // Add event listener to capture phase to intercept before react-grid-layout
    document.addEventListener('mousedown', handleMouseDown, true);
    
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, []);

  // Combined grid props - simplified, no custom drag handling
  const combinedGridProps = {
    className: 'layout',
    breakpoints,
    cols: defaultCols,
    layouts,
    width: containerWidth,
    onLayoutChange: handleLayoutChange,
    onBreakpointChange: handleBreakpointChange,
    isDraggable,
    isResizable,
    dragHandleClass: 'grid-drag-handle',
    draggableHandle: '.grid-drag-handle',
    draggableCancel: 'canvas, [class*="cytoscape"], .no-drag, input, select, textarea, button, .fc, .fc *, .resource-calendar, .resource-calendar *', // Prevent dragging when clicking on interactive/cancel elements or FullCalendar/Cytoscape internals
    useCSSTransforms: true,
    preventCollision: false,
    compactType: 'vertical',
    margin: [16, 16],
    containerPadding: [16, 16],
    rowHeight: 60,
    autoSize: true,
    ...gridProps
  };




  return (
    <div ref={containerRef} className="grid-layout-container w-full" {...otherProps}>

      <ResponsiveGridLayout {...combinedGridProps}>
        {children}
      </ResponsiveGridLayout>
    </div>
  );
};

export default GridLayout;

/**
 * Utility function to create grid items with proper data-grid attributes
 * 
 * @param {string} id - Unique identifier for the grid item
 * @param {Object} layout - Layout configuration
 * @param {React.ReactNode} children - Component to wrap
 * @param {Object} gridComponentProps - Props to pass to GridComponent
 * @returns {React.ReactElement} Grid item component
 */
export const createGridItem = (id, layout, children, gridComponentProps = {}) => {
  const dataGrid = {
    i: id,
    x: layout.x || 0,
    y: layout.y || 0,
    w: layout.w || 6,
    h: layout.h || 4,
    minW: layout.minW || 3,
    minH: layout.minH || 2,
    maxW: layout.maxW || 12,
    maxH: layout.maxH || 12,
    ...layout
  };

  return (
    <div key={id} data-grid={dataGrid}>
      <GridComponent {...gridComponentProps}>
        {children}
      </GridComponent>
    </div>
  );
};

/**
 * Hook for managing grid component visibility/enabled state
 * 
 * @param {Array} initialComponents - Array of component IDs that should be visible initially
 * @param {string} storageKey - localStorage key for persistence
 * @returns {Object} { visibleComponents, toggleComponent, showComponent, hideComponent }
 */
export const useGridVisibility = (initialComponents = [], storageKey) => {
  const [visibleComponents, setVisibleComponents] = useState(() => {
    if (!storageKey) return new Set(initialComponents);
    
    try {
      const saved = localStorage.getItem(`gridVisibility_${storageKey}`);
      if (saved) {
        const savedSet = new Set(JSON.parse(saved));
        // Ensure any newly added default components become visible by default
        initialComponents.forEach((comp) => savedSet.add(comp));
        return savedSet;
      }
      return new Set(initialComponents);
    } catch {
      return new Set(initialComponents);
    }
  });

  // Save to localStorage whenever visibility changes
  useEffect(() => {
    if (!storageKey) return;
    
    try {
      localStorage.setItem(
        `gridVisibility_${storageKey}`, 
        JSON.stringify(Array.from(visibleComponents))
      );
    } catch (error) {
      console.warn('Failed to save grid visibility to localStorage:', error);
    }
  }, [visibleComponents, storageKey]);

  const toggleComponent = useCallback((componentId) => {
    setVisibleComponents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(componentId)) {
        newSet.delete(componentId);
      } else {
        newSet.add(componentId);
      }
      return newSet;
    });
  }, []);

  const showComponent = useCallback((componentId) => {
    setVisibleComponents(prev => new Set([...prev, componentId]));
  }, []);

  const hideComponent = useCallback((componentId) => {
    setVisibleComponents(prev => {
      const newSet = new Set(prev);
      newSet.delete(componentId);
      return newSet;
    });
  }, []);

  return {
    visibleComponents,
    toggleComponent,
    showComponent,
    hideComponent,
    isVisible: (componentId) => visibleComponents.has(componentId)
  };
};